import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import ConsoleWrapper from '@/docs-partials/endpoints/console';
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <h2>{`Search Endpoint`}</h2>
    <p>{`GIPHY Search gives you instant access to our library of millions of GIFs and Stickers by entering a word or phrase. With our unparalleled search algorithm, users can easily express themselves and animate their conversations.`}</p>
    <p>
  <ul style={{
        "listStyle": "inherit",
        "listStylePosition": "inside"
      }}>
    <li>GIPHY requires the Search API call be made from the client side.</li>
    <li>
      The search keyword should be sent to GIPHY in <span style={{
            "fontFamily": "'PT Mono'"
          }}>&q</span> parameter in the API call. Search terms could simply be words or phrases typed by
      the user. Users can also add the @ sign before a GIPHY username to return content from a specific GIPHY channel. All special characters should be supported and not re-encoded
      in your search request.
    </li>
    <li>This keyword should be the exact terms the user searched for without any correction/enhancement</li>
    <li>
      Optionally, use the <span style={{
            "fontFamily": "'PT Mono'"
          }}>&lang</span> parameter to indicate the language the user has typed in. This will help return unique regional content,
      if available. See <a href="/docs/optional-settings/#language-support">here</a> for supported languages.
    </li>
    <li>
      You may use the <span style={{
            "fontFamily": "'PT Mono'"
          }}>&rating</span> param to tailor the response per your preferences. Read more{' '}
      <a href="/docs/optional-settings#rating">here</a> about content ratings
    </li>
    <li>
      Optionally, you can use <a href="/docs/optional-settings#renditions-on-demand">rendition on demand</a> bundles to limit which renditions the API sends.
    </li>
  </ul>
    </p>
    <ConsoleWrapper mdxType="ConsoleWrapper">
      <table>
        <thead parentName="table">
          <tr parentName="thead">
            <th parentName="tr" {...{
              "align": "left"
            }}>{`Gif URL`}</th>
            <th parentName="tr" {...{
              "align": "left"
            }}>{`Sticker URL`}</th>
          </tr>
        </thead>
        <tbody parentName="table">
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": "left"
            }}>{`api.giphy.com/v1/gifs/search`}</td>
            <td parentName="tr" {...{
              "align": "left"
            }}>{`api.giphy.com/v1/stickers/search`}</td>
          </tr>
        </tbody>
      </table>
    </ConsoleWrapper>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Request Parameters:`}</th>
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Example:`}</th>
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Description:`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><strong parentName="td">{`api_key:`}</strong>{` `}<em parentName="td">{`string(required)`}</em></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`YOUR_API_KEY`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`GIPHY API Key.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><strong parentName="td">{`q:`}</strong>{` `}<em parentName="td">{`string(required)`}</em></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`cheeseburgers`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Search query term or phrase. Adding @`}{`<`}{`username`}{`>`}{` anywhere in the q parameter effectively changes the search query to be a search for a specific user’s GIFs (user has to be public and verified user by GIPHY.) `}<br />{` Maximum length: 50 chars.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><strong parentName="td">{`limit:`}</strong>{` `}<em parentName="td">{`integer (int32)`}</em></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`20`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`The maximum number of objects to return. (Default: “25”).`}<br />{` `}<inlineCode parentName="td">{`For beta keys max limit is 50`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><strong parentName="td">{`offset:`}</strong>{` `}<em parentName="td">{`integer (int32)`}</em></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`5`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Specifies the starting position of the results.`}<br />{` Default: “0”`}<br />{` Maximum: “4999”`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><strong parentName="td">{`rating:`}</strong>{` `}<em parentName="td">{`string`}</em></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`g`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Filters results by `}<a href="/docs/optional-settings#rating">{`specified rating`}</a>{`. Acceptable values include g, pg, pg-13, r. If you do not specify a rating, you will receive results from all possible ratings.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><strong parentName="td">{`lang:`}</strong>{` `}<em parentName="td">{`string`}</em></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`en`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Specify `}<a href="/docs/optional-settings#language-support">{`default language`}</a>{` for regional content; use a 2-letter ISO 639-1 language code.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><strong parentName="td">{`random_id:`}</strong>{` `}<em parentName="td">{`string`}</em></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`e826c9fc5c929e0d6c6d423841a282aa`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`An ID/proxy for a specific user.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><strong parentName="td">{`bundle:`}</strong>{` `}<em parentName="td">{`string`}</em></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`messaging_non_clips`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Returns only renditions that correspond to the named bundle. `}<a href='/docs/optional-settings#renditions-on-demand'>{`Read more about renditions.`}</a></td>
        </tr>
      </tbody>
    </table>
    <h5>{`Successful Response (200 OK)`}</h5>
    <ConsoleWrapper mdxType="ConsoleWrapper">
  <ul>
    <li>
      data: <a href="/docs/api/schema#gif-object">GIF Object[]</a>
    </li>
    <li>
      pagination: <a href="/docs/api/schema#pagination-object">Pagination Object</a>
    </li>
    <li>
      meta: <a href="/docs/api/schema#meta-object">Meta Object</a>
    </li>
  </ul>
    </ConsoleWrapper>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      